import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchLeadershipMessages } from '../lib/api.js';
import { SectionHead } from './SectionHead.jsx';

/**
 * "Messages from leadership" — magazine-style editorial spread. Each
 * leader gets the full container width, alternating which side the
 * portrait sits on (left for even rows, right for odd rows). Styles
 * live in web/src/assets/homepage.css under `.leader-spread`.
 *
 * Returns null if no leadership messages are configured.
 */
export function LeadershipMessages() {
  const { data: messages } = useApi(fetchLeadershipMessages, []);
  const list = Array.isArray(messages) ? messages : [];
  if (list.length === 0) return null;

  return (
    <section className="bg-cream">
      <div className="container">
        <SectionHead
          align="center"
          eyebrow="From our leadership"
          rawTitle="Messages from the <em>people who shape Maple Leaf</em>"
          lede="A school is its people. Hear directly from the leaders who guide our community every day."
        />

        <div className="leader-spread">
          {list.map((m, idx) => {
            const flip = idx % 2 === 1;
            return (
              <article
                key={m.id ?? m.name}
                className="leader-row"
                data-flip={String(flip)}
              >
                <div
                  className="leader-portrait"
                  style={m.photo ? { backgroundImage: `url('${m.photo}')` } : undefined}
                  aria-hidden="true"
                />
                <div className="leader-copy">
                  {m.label && <span className="leader-label">{m.label}</span>}
                  <span className="leader-quote-mark" aria-hidden="true">&ldquo;</span>
                  {m.excerpt && <p className="leader-quote">{m.excerpt}</p>}
                  <span className="leader-rule" aria-hidden="true" />
                  <span className="leader-name">{m.name}</span>
                  {m.role && <span className="leader-role">{m.role}</span>}
                  {m.link && (
                    <Link to={m.link} className="leader-readmore">
                      Read full message
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
